import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { CAMP_TO_PAGE_ID, cameFromExternalRedirect, getLastPageId } from "./common/utils/lastPage";
import { logger, redactParams } from "./common/utils/logger";
import { trackPageView } from "./common/utils/analytics";
import RouteErrorBoundary from "./common/components/RouteErrorBoundary";

const LandingPageSuccess = lazy(() => import("./pages/default/SuccessPage"));
const LandingPageFailed = lazy(() => import("./pages/default/FailedPage"));
const LandingPageTerms = lazy(() => import("./pages/default/TermsPage"));

const ZainLandingPageHeaderFlow = lazy(() => import("./pages/default/ZainHeaderPage"));
const AsiaCellLandingPageHeaderFlow = lazy(() => import("./pages/default/AsiaCellHeaderPage"));
const AsiaCellLandingPageConfirmFlow = lazy(() => import("./pages/default/AsiaCellConfirmPage"));
const LandingPagePinFlow = lazy(() => import("./pages/default/PinPage"));

const ZainPage7812 = lazy(() => import("./pages/7812/ZainHeaderPage"));
const Page7812Pin = lazy(() => import("./pages/7812/PinPage"));
const Page7812Success = lazy(() => import("./pages/7812/SuccessPage"));
const Page7812Failed = lazy(() => import("./pages/7812/FailedPage"));

const ZainPage2231 = lazy(() => import("./pages/2231/ZainHeaderPage"));
const Page2231Pin = lazy(() => import("./pages/2231/PinPage"));
const Page2231Success = lazy(() => import("./pages/2231/SuccessPage"));
const Page2231Failed = lazy(() => import("./pages/2231/FailedPage"));

const ZainPage6364 = lazy(() => import("./pages/6364/ZainHeaderPage"));
const Page6364Pin = lazy(() => import("./pages/6364/PinPage"));
const Page6364Success = lazy(() => import("./pages/6364/SuccessPage"));
const Page6364Failed = lazy(() => import("./pages/6364/FailedPage"));

const ZainPage8609 = lazy(() => import("./pages/8609/ZainHeaderPage"));
const Page8609Pin = lazy(() => import("./pages/8609/PinPage"));
const Page8609Success = lazy(() => import("./pages/8609/SuccessPage"));
const Page8609Failed = lazy(() => import("./pages/8609/FailedPage"));

const ZainPage1802 = lazy(() => import("./pages/1802/ZainHeaderPage"));
const Page1802Pin = lazy(() => import("./pages/1802/PinPage"));
const Page1802Success = lazy(() => import("./pages/1802/SuccessPage"));
const Page1802Failed = lazy(() => import("./pages/1802/FailedPage"));

const ZainPage3813 = lazy(() => import("./pages/3813/ZainHeaderPage"));
const Page3813Pin = lazy(() => import("./pages/3813/PinPage"));
const Page3813Success = lazy(() => import("./pages/3813/SuccessPage"));
const Page3813Failed = lazy(() => import("./pages/3813/FailedPage"));

const ZainPage4237 = lazy(() => import("./pages/4237/ZainHeaderPage"));
const Page4237Pin = lazy(() => import("./pages/4237/PinPage"));
const Page4237TermsCondition = lazy(() => import("./pages/4237/TermsPage"));
const Page4237Success = lazy(() => import("./pages/4237/SuccessPage"));
const Page4237Failed = lazy(() => import("./pages/4237/FailedPage"));

const zainHePages = {
  "7812": ZainPage7812,
  "2231": ZainPage2231,
  "4237": ZainPage4237,
  "8609": ZainPage8609,
  "6364": ZainPage6364,
  "1802": ZainPage1802,
  "3813": ZainPage3813,
};

const pinRoutes = [
  { path: "/7812/pin", Component: Page7812Pin },
  { path: "/4237/pin", Component: Page4237Pin },
  { path: "/6364/pin", Component: Page6364Pin },
  { path: "/2231/pin", Component: Page2231Pin },
  { path: "/1802/pin", Component: Page1802Pin },
  { path: "/3813/pin", Component: Page3813Pin },
  { path: "/8609/pin", Component: Page8609Pin },
];

const resultRoutes = [
  { path: "/7812/success", Component: Page7812Success },
  { path: "/7812/failed", Component: Page7812Failed },
  { path: "/2231/success", Component: Page2231Success },
  { path: "/2231/failed", Component: Page2231Failed },
  { path: "/6364/success", Component: Page6364Success },
  { path: "/6364/failed", Component: Page6364Failed },
  { path: "/8609/success", Component: Page8609Success },
  { path: "/8609/failed", Component: Page8609Failed },
  { path: "/1802/success", Component: Page1802Success },
  { path: "/1802/failed", Component: Page1802Failed },
  { path: "/3813/success", Component: Page3813Success },
  { path: "/3813/failed", Component: Page3813Failed },
  { path: "/4237/success", Component: Page4237Success },
  { path: "/4237/failed", Component: Page4237Failed },
];

/**
 * Resolves a human-readable page name for every route this app serves —
 * e.g. "LP 7812 — Zain Header", "LP 7812 — PIN Entry", "Default LP —
 * Success" — so logs read like a product person's description of the page,
 * not a raw path. `/lp/he` is the one ambiguous route (it renders either
 * operator's header, decided by isAsiaCellHeader — already resolved in App()
 * below), so that is passed in rather than re-derived here.
 */
function resolvePageName(pathname, { pageId, isAsiaCellHeader }) {
  const lpLabel = pageId ? `LP ${pageId}` : "Default LP";

  if (pathname === "/lp/he") {
    return `${lpLabel} — ${isAsiaCellHeader ? "AsiaCell Header" : "Zain Header"}`;
  }

  if (pathname === "/lp/confirm") return "Default LP — AsiaCell Confirm";

  const pinMatch = pathname.match(/^\/(\d+)\/pin$/);
  if (pinMatch) return `LP ${pinMatch[1]} — PIN Entry`;
  if (pathname === "/lp/pin") return "Default LP — PIN Entry";

  const successMatch = pathname.match(/^\/(\d+)\/success$/);
  if (successMatch) return `LP ${successMatch[1]} — Success`;
  if (pathname === "/lp/success") return "Default LP — Success";

  const failedMatch = pathname.match(/^\/(\d+)\/failed$/);
  if (failedMatch) return `LP ${failedMatch[1]} — Failed`;
  if (pathname === "/lp/failed") return "Default LP — Failed";

  const termsMatch = pathname.match(/^\/(\d+)\/terms$/);
  if (termsMatch) return `LP ${termsMatch[1]} — Terms & Conditions`;
  if (pathname === "/terms") return "Default LP — Terms & Conditions";

  return pathname;
}

/**
 * Logs a page-view on every route change and sets the ambient page name
 * every subsequent log line will carry (see logger.setPageName) — mounted
 * once inside <Router> so every page component across all numbered LPs gets
 * tracked without needing its own logging call. Query params are redacted
 * (MSISDN/OTP/etc) before shipping.
 */
function RouteViewLogger({ pageId, isAsiaCellHeader }) {
  const location = useLocation();

  useEffect(() => {
    const pageName = resolvePageName(location.pathname, { pageId, isAsiaCellHeader });
    logger.setPageName(pageName, location.pathname);

    logger.info("page_view", "route_change", `Visitor opened the "${pageName}" page`, {
      path: location.pathname,
      query: redactParams(Object.fromEntries(new URLSearchParams(location.search))),
    });

    // GA4 — one page_view per route (see common/utils/analytics.js).
    trackPageView(location.pathname, location.search, pageName);
  }, [location.pathname, location.search, pageId, isAsiaCellHeader]);

  return null;
}

function App({ ti, ts, camp, isAsiaCellHe }) {
  // Falls back to the page id persisted in localStorage (by
  // public/index.html on the original visit) when the current URL can't
  // resolve one — covers a Zain carrier round-trip back to /lp/he that
  // carries no campaignId. Without the fallback, the default Header
  // component would render instead of the originally-visited numbered
  // page's, sending failures to the generic /lp/pin instead of that page's
  // own /pin route. Gated behind cameFromExternalRedirect() (referrer-based)
  // so a fresh, campaign-less /lp/he visit (bookmark, manual URL, closed-and
  // -reopened tab) doesn't incorrectly inherit whatever numbered LP this
  // browser last touched. getLastPageId() itself also expires the stored
  // value after MAX_AGE_MS as a second, independent guard.
  const pageId = CAMP_TO_PAGE_ID[camp] || (cameFromExternalRedirect() ? getLastPageId() : null);
  const ZainHeaderComponent = zainHePages[pageId] || ZainLandingPageHeaderFlow;
  // AsiaCell is default-LP-only — never true for a numbered campaign page
  // (pageId truthy), regardless of what network the visitor is actually on,
  // so every numbered page's dispatch below is completely unaffected by this
  // flag. isAsiaCellHe itself is only ever true when public/index.html's
  // network detection found no Zain network AND an AsiaCell one (Zain is
  // always checked first), so a Zain visitor can never reach this branch.
  const isDefaultAsiaCellHe = Boolean(isAsiaCellHe && !pageId);

  return (
    <Router>
      <RouteViewLogger pageId={pageId} isAsiaCellHeader={isDefaultAsiaCellHe} />
      <RouteErrorBoundary>
      <Suspense fallback={null}>
      <Routes>
        <Route
          path="/lp/he"
          element={
            isDefaultAsiaCellHe ? (
              // AsiaCell, default LP only (see isDefaultAsiaCellHe above) —
              // every other case falls through to the unchanged Zain
              // component, exactly as before this branch existed.
              <AsiaCellLandingPageHeaderFlow />
            ) : (
              <ZainHeaderComponent ti={ti} ts={ts} />
            )
          }
        />

        {/* AsiaCell header flow, step 2 — see AsiaCellHeaderPage.js's doc comment. Default-LP-only, same as /lp/he's AsiaCell branch above. */}
        <Route path="/lp/confirm" element={<AsiaCellLandingPageConfirmFlow />} />

        <Route path="/lp/pin" element={<LandingPagePinFlow />} />

        {pinRoutes.map(({ path, Component }) => (
          <Route
            key={path}
            path={path}
            element={
              <Component
                ti={ti}
                ts={ts}
              />
            }
          />
        ))}

        {resultRoutes.map(({ path, Component }) => (
          <Route key={path} path={path} element={<Component />} />
        ))}

        <Route path="/lp/success" element={<LandingPageSuccess />} />
        <Route path="/lp/failed" element={<LandingPageFailed />} />
        <Route path="/terms" element={<LandingPageTerms />} />
        <Route path="/4237/terms" element={<Page4237TermsCondition />} />
      </Routes>
      </Suspense>
      </RouteErrorBoundary>
    </Router>
  );
}

export default App;
